// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for deleting files associated with assets.
 * Provides functionality to remove file attachments from a specific asset.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { successResponseSchema } from '@/api/schemas';

/**
 * Deletes an existing file from an asset.
 * This function handles the API call to remove a file attachment and validates
 * the response data using Zod schemas.
 *
 * @param assetKey - Unique identifier of the asset to delete the file from
 * @param fileKey - Unique identifier of the file to delete
 * @returns Promise resolving to the success response
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function deleteAssetFile(assetKey: string, fileKey: string) {
  const path = `/v1/asset/${assetKey}/file/${fileKey}`;
  const response = await api.delete(path);

  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'DeleteAssetFile',
    endpoint: path,
  });
}
